import React, { useState } from "react";
import { CustomRangePopover } from "../../internalDashboard/components";
import { useCommonParams } from "../../../hooks/useCommonParams";

// Segment control that writes the usage period to the URL: `?period=` for
// the presets, `?from&to` for a custom range (the API's resolvePeriod reads
// either). Shared by the Brand Usage list and detail pages.
export const PERIOD_OPTIONS: Array<{ label: string; value: string }> = [
    { label: "Today", value: "today" },
    { label: "7d", value: "7d" },
    { label: "30d", value: "30d" },
    { label: "Quarter", value: "quarter" },
    { label: "Year", value: "year" },
    { label: "Custom", value: "custom" },
];

export function useUsagePeriod() {
    const { getFilter } = useCommonParams();
    const from = getFilter("from") || "";
    const to = getFilter("to") || "";
    const period = from && to ? "custom" : getFilter("period") || "30d";
    const extraParams =
        period === "custom" ? { from: from || undefined, to: to || undefined } : { period };
    return { from, to, period, extraParams };
}

export function UsagePeriodPicker() {
    const { setSearchParams } = useCommonParams();
    const { from, to, period } = useUsagePeriod();
    const [customAnchor, setCustomAnchor] = useState<HTMLElement | null>(null);

    const pickPeriod = (value: string, anchor?: HTMLElement) => {
        if (value === "custom") {
            if (anchor) setCustomAnchor(anchor);
            return;
        }
        setSearchParams((prev) => {
            prev.delete("from");
            prev.delete("to");
            prev.delete("page");
            prev.set("period", value);
            return prev;
        });
    };

    return (
        <div className="h-[38px] box-border flex bg-surface-2 rounded-[9px] p-1">
            {PERIOD_OPTIONS.map((p) => {
                const isActive = period === p.value;
                const label = p.value === "custom" && from && to ? `${from} – ${to}` : p.label;
                return (
                    <button
                        key={p.value}
                        type="button"
                        onClick={(e) => pickPeriod(p.value, e.currentTarget)}
                        className={`h-full px-3 text-[12px] rounded-md flex items-center cursor-pointer transition-all ${
                            isActive
                                ? "bg-surface text-text-1 font-semibold shadow-[shadow:var(--sh-1)]"
                                : "bg-transparent text-text-2 font-medium"
                        }`}
                    >
                        {label}
                    </button>
                );
            })}
            <CustomRangePopover
                anchor={customAnchor}
                onClose={() => setCustomAnchor(null)}
                onApply={(f, t) => {
                    setCustomAnchor(null);
                    setSearchParams((prev) => {
                        prev.delete("period");
                        prev.delete("page");
                        prev.set("from", f);
                        prev.set("to", t);
                        return prev;
                    });
                }}
            />
        </div>
    );
}
